import React, { useState } from "react";
import { ModalProps } from "../../../Shared/types/ModalProps";
import { GenericButton } from "../../../Shared/components/GenericButton";
import {
  GenericFileInput,
  GenericInput,
  GenericMultipleFileInput,
} from "../../../Shared/components/GenericInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { CreateProfileInput } from "../models/CreateProfileInput";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { IProfile } from "../models/Profile";
import { ProfileService } from "../services/profile.service";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { ICompany } from "../models/Company";
import { FileModel } from "../../../Shared/dto/create-file-input";
import { useTranslation } from "react-i18next";

type ProfileFormProps = ModalProps & {
  profile?: IProfile;
  company: ICompany;
};

export const ProfileForm: React.FC<ProfileFormProps> = ({
  onClose,
  profile,
  company,
}) => {
  const { t } = useTranslation();
  const message = t('loading_message');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const [picture, setPicture] = useState<FileModel | null>(
    UtilService.attachmentsToFileModel(profile?.picture)?.[0] || null
  );
  const [pictureError, setPictureError] = useState("");
  const [coverPhoto, setCoverPhoto] = useState<FileModel | null>(
    UtilService.attachmentsToFileModel(profile?.coverPhoto)?.[0] || null
  );
  const [coverPhotoError, setCoverPhotoError] = useState("");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [email, setEmail] = useState(profile?.email || "");
  const [emailError, setEmailError] = useState("");
  const [registrationDate, setRegistrationDate] = useState(
    profile?.registrationDate || ""
  );
  const [website, setWebsite] = useState(profile?.website || "");
  const [websiteError, setWebsiteError] = useState("");

  const [contactFromFullName, setContactFromFullName] = useState(
    profile?.contactFromFullName || ""
  );
  const [contactFromFullNameError, setContactFromFullNameError] = useState("");
  const [contactFromEmail, setContactFromEmail] = useState(
    profile?.contactFromEmail || ""
  );
  const [contactFromEmailError, setContactFromEmailError] = useState("");
  const [contactFromPhone, setContactFromPhone] = useState(
    profile?.contactFromPhone || ""
  );
  const [contactFromPhoneError, setContactFromPhoneError] = useState("");
  const [contactFromAddress, setContactFromAddress] = useState(
    profile?.contactFromAddress || ""
  );
  const [contactFromAddressError, setContactFromAddressError] = useState("");

  const [contactToFullName, setContactToFullName] = useState(
    profile?.contactToFullName || ""
  );
  const [contactToFullNameError, setContactToFullNameError] = useState("");
  const [contactToEmail, setContactToEmail] = useState(
    profile?.contactToEmail || ""
  );
  const [contactToEmailError, setContactToEmailError] = useState("");
  const [contactToPhone, setContactToPhone] = useState(
    profile?.contactToPhone || ""
  );
  const [contactToPhoneError, setContactToPhoneError] = useState("");
  const [contactToAddress, setContactToAddress] = useState(
    profile?.contactToAddress || ""
  );
  const [contactToAddressError, setContactToAddressError] = useState("");

  const [contactFromAttachments, setContactFromAttachments] = useState<
    FileModel[]
  >(UtilService.attachmentsToFileModel(profile?.contactFromAttachments) || []);
  const [contactFromAttachmentsError, setContactFromAttachmentsError] =
    useState("");

  const [contactToAttachments, setContactToAttachments] = useState<FileModel[]>(
    UtilService.attachmentsToFileModel(profile?.contactToAttachments) || []
  );
  const [contactToAttachmentsError, setContactToAttachmentsError] =
    useState("");

  const onContactFromFullName = (val: string) => {
    setContactFromFullName(val);
    setContactFromFullNameError(
      val.trim() === "" ? t('full_name_required') : ""
    );
  };

  const onContactFromEmail = (val: string) => {
    setContactFromEmail(val);
    setContactFromEmailError(val.trim() === "" ? t('email_required') : "");
  };

  const onContactFromPhone = (val: string) => {
    setContactFromPhone(val);
    setContactFromPhoneError(val.trim() === "" ? t('phone_required') : "");
  };

  const onContactFromAddress = (val: string) => {
    setContactFromAddress(val);
    setContactFromAddressError(val.trim() === "" ? t('address_required') : "");
  };
  const onContactToFullName = (val: string) => {
    setContactToFullName(val);
    setContactToFullNameError(val.trim() === "" ? t('full_name_required') : "");
  };

  const onContactToEmail = (val: string) => {
    setContactToEmail(val);
    setContactToEmailError(val.trim() === "" ? t('email_required') : "");
  };

  const onContactToPhone = (val: string) => {
    setContactToPhone(val);
    setContactToPhoneError(val.trim() === "" ? t('phone_required') : "");
  };

  const onContactToAddress = (val: string) => {
    setContactToAddress(val);
    setContactToAddressError(val.trim() === "" ? t('address_required') : "");
  };

  const onPicture = async (file: File) => {
    const response = await UtilService.onAttachFile(file, setPictureError);
    if (response) {
      setPicture(response);
    }
  };

  const onCoverPhoto = async (file: File) => {
    const response = await UtilService.onAttachFile(file, setCoverPhotoError);
    if (response) {
      setCoverPhoto(response);
    }
  };

  const onPhone = (val: string) => setPhone(val);
  const onEmail = (val: string) => {
    setEmail(val);
    setEmailError(/\S+@\S+\.\S+/.test(val) ? "" : t('invalid_email'));
  };

  const onRegistrationDate = (val: string) => setRegistrationDate(val);
  const onWebsite = (val: string) => setWebsite(val);

  const onContactFromAttachments = async (files: File[]) => {
    let base64Files = await Promise.all(
      files.map((file) =>
        UtilService.onAttachFile(file, setContactFromAttachmentsError)
      )
    );
    if (base64Files?.length > 0) {
      setContactFromAttachments(
        base64Files.filter(
          (file): file is FileModel => file !== undefined
        ) as FileModel[]
      );
    }
  };

  const onContactToAttachments = async (files: File[]) => {
    let base64Files = await Promise.all(
      files.map((file) =>
        UtilService.onAttachFile(file, setContactToAttachmentsError)
      )
    );
    if (base64Files?.length > 0) {
      setContactToAttachments(
        base64Files.filter(
          (file): file is FileModel => file !== undefined
        ) as FileModel[]
      );
    }
  };

  const resetForm = () => {
    setPicture(null);
    setCoverPhoto(null);
    setPhone("");
    setEmail("");
    setEmailError("");
    setRegistrationDate("");
    setWebsite("");
    setWebsiteError("");

    setContactFromFullName("");
    setContactFromFullNameError("");
    setContactFromEmail("");
    setContactFromEmailError("");
    setContactFromPhone("");
    setContactFromPhoneError("");
    setContactFromAddress("");
    setContactFromAddressError("");
    setContactFromAttachments([]);
    setContactFromAttachmentsError("");

    setContactToFullName("");
    setContactToFullNameError("");
    setContactToEmail("");
    setContactToEmailError("");
    setContactToPhone("");
    setContactToPhoneError("");
    setContactToAddress("");
    setContactToAddressError("");
    setContactToAttachments([]);
    setContactToAttachmentsError("");
  };

  const isFromFullNameValid = contactFromFullName.trim() !== "";
  const isToFullNameValid = contactToFullName.trim() !== "";
  const isFormValid = isFromFullNameValid && isToFullNameValid;

  const createAction = async (e: React.FormEvent) => {
    e.preventDefault();
    const profileInput = new CreateProfileInput();
    if (profile?.id) {
      profileInput.id = profile?.id;
    }
    profileInput.companyId = company.id;
    profileInput.phone = phone;
    profileInput.email = email;
    profileInput.registrationDate = registrationDate;
    profileInput.website = website;
    profileInput.picture = picture ?? undefined;
    profileInput.coverPhoto = coverPhoto ?? undefined;

    // Contact From
    profileInput.contactFromFullName = contactFromFullName;
    profileInput.contactFromEmail = contactFromEmail;
    profileInput.contactFromPhone = contactFromPhone;
    profileInput.contactFromAddress = contactFromAddress;
    profileInput.contactFromAttachments = contactFromAttachments;

    // Contact To
    profileInput.contactToFullName = contactToFullName;
    profileInput.contactToEmail = contactToEmail;
    profileInput.contactToPhone = contactToPhone;
    profileInput.contactToAddress = contactToAddress;
    profileInput.contactToAttachments = contactToAttachments;

    try {
      setIsLoading(true);
      await ProfileService.create(profileInput);
      resetForm();
      onClose(true);
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-xl p-5 mt-10 mb-10 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {profile?.id ? t('update_profile') : t('create_profile')}
            </h3>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="relative">
            <div className="w-full h-90">
              <form className="mt-3 mb-3" onSubmit={createAction}>
                <div className="italic font-bold">{t('company_information')}</div>

                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericFileInput
                    containerClassName=""
                    label={t('company_logo_label')}
                    onChange={onPicture}
                    placeholder={t('company_logo_hint')}
                    required={false}
                    error={pictureError}
                  />

                  <GenericFileInput
                    containerClassName=""
                    label={t('company_feature_image_label')}
                    onChange={onCoverPhoto}
                    placeholder={t('enter_cover_photo_hint')}
                    required={false}
                    error={coverPhotoError}
                  />
                </div>

                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericInput
                    containerClassName=""
                    label={t('phone_number_label')}
                    value={phone}
                    onChange={onPhone}
                    type="text"
                    placeholder={t('phone_number_hint')}
                    required={true}
                  />

                  <GenericInput
                    containerClassName=""
                    label={t('email_label')}
                    value={email}
                    onChange={onEmail}
                    type="email"
                    error={emailError}
                    placeholder={t('email_hint')}
                    required={true}
                  />
                </div>

                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericInput
                    containerClassName=""
                    label={t('registration_date')}
                    value={registrationDate}
                    onChange={onRegistrationDate}
                    type="date"
                    placeholder={t('enter_registration_date_hint')}
                    required={true}
                  />
                  <GenericInput
                    containerClassName=""
                    label={t('website_link_label')}
                    value={website}
                    onChange={onWebsite}
                    type="url"
                    placeholder={t('website_link_hint')}
                    error={websiteError}
                    required={false}
                  />
                </div>

                <div className="italic font-bold">{t('contact_person_in')} {company.country}</div>
                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericInput
                    containerClassName=""
                    label={t('full_name_label')}
                    value={contactFromFullName}
                    onChange={onContactFromFullName}
                    type="text"
                    error={contactFromFullNameError}
                    placeholder={t('full_name_hint')}
                    required={true}
                  />

                  <GenericInput
                    containerClassName=""
                    label={t('phone_number_label')}
                    value={contactFromPhone}
                    onChange={onContactFromPhone}
                    type="text"
                    error={contactFromPhoneError}
                    placeholder={t('phone_number_hint')}
                    required={true}
                  />
                </div>

                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericInput
                    containerClassName=""
                    label={t('email_label')}
                    value={contactFromEmail}
                    onChange={onContactFromEmail}
                    type="email"
                    error={contactFromEmailError}
                    placeholder={t('email_hint')}
                    required={true}
                  />

                  <GenericInput
                    containerClassName=""
                    label={t('address')}
                    value={contactFromAddress}
                    onChange={onContactFromAddress}
                    type="text"
                    error={contactFromAddressError}
                    placeholder={t('address_hint')}
                    required={true}
                  />
                </div>

                {/* <GenericMultipleFileInput
                  label="Attachments(License, Business Registration)"
                  onChange={onContactFromAttachments}
                  error={contactFromAttachmentsError}
                  placeholder="Attachments(License, Business Registration)"
                  required={true}
                /> */}

                <div className="italic font-bold">{t('contact_person_in_drc')}</div>
                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericInput
                    containerClassName=""
                    label={t('full_name_label')}
                    value={contactToFullName}
                    onChange={onContactToFullName}
                    type="text"
                    error={contactToFullNameError}
                    placeholder={t('full_name_hint')}
                    required={true}
                  />

                  <GenericInput
                    containerClassName=""
                    label={t('phone_number_label')}
                    value={contactToPhone}
                    onChange={onContactToPhone}
                    type="text"
                    error={contactToPhoneError}
                    placeholder={t('phone_number_hint')}
                    required={true}
                  />
                </div>

                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericInput
                    containerClassName=""
                    label={t('email_label')}
                    value={contactToEmail}
                    onChange={onContactToEmail}
                    type="email"
                    error={contactToEmailError}
                    placeholder={t('email_hint')}
                    required={true}
                  />

                  <GenericInput
                    containerClassName=""
                    label={t('address')}
                    value={contactToAddress}
                    onChange={onContactToAddress}
                    type="text"
                    error={contactToAddressError}
                    placeholder={t('address_hint')}
                    required={true}
                  />
                </div>
                <GenericMultipleFileInput
                  label={t('attachments')}
                  onChange={onContactToAttachments}
                  error={contactToAttachmentsError}
                  placeholder={t('drc_contact_attachments')}
                  required={true}
                />

                <div className="mx-5">
                  <GenericButton type="submit" disabled={!isFormValid}>
                    {profile?.id ? t('update_profile') : t('create_profile')}
                  </GenericButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
